import { AnimatePresence, motion } from 'motion/react'
import { Fragment, ReactNode, useEffect, useRef, useState } from 'react'
import { AnimateChangeInHeight, Jobibot } from '../../rapid-ui'

type MessageType = 'bot' | 'loader' | 'multi-loader'

type BotMessage = {
	type: 'bot'
	content: string
}

type MultiLoaderPayload = Array<{
	content: string
	doneContent: string
	state: 'pending' | 'done'
}>

export type ChatMessage =
	& {
		id: string
		sentAt?: Date
	}
	& (
		{
			type: 'bot'
			content: string
		} | {
			type: 'loader'
			contents: Array<string>
			state: 'pending' | 'done'
			doneContent: string
		} | {
			type: 'multi-loader'
			loaders: MultiLoaderPayload
		}
	)

export type EditPanelChatProps = {
	messages: ChatMessage[]
}
export function EditPanelChat({ messages }: EditPanelChatProps) {
	return (
		<div className="flex flex-col gap-2 mt-4">
			{messages.map((message, i, a) => (
				<Fragment key={message.id}>
					{message.type === 'bot' && <BotMessage previousType={a[i - 1]?.type}>{message.content}</BotMessage>}
					{message.type === 'loader'
						&& (
							<BotMessage previousType={a[i - 1]?.type}>
								<Loader
									contents={message.contents}
									state={message.state}
									doneContent={message.doneContent}
								/>
							</BotMessage>
						)}
					{message.type === 'multi-loader'
						&& (
							<BotMessage previousType={a[i - 1]?.type}>
								<MultiLoader loaders={message.loaders} />
							</BotMessage>
						)}
				</Fragment>
			))}
		</div>
	)
}

function BotMessage({ children, previousType }: { children: ReactNode; previousType: MessageType }) {
	return (
		<div className="grid grid-cols-[auto_1fr] gap-2 mr-[10%]">
			<div className="w-6 mt-1.5">
				{previousType !== 'bot' && <Jobibot size={24} />}
			</div>
			<div className="bg-grey-50 rounded-sm px-3 py-2 text-grey-700 text-sm">
				{children}
			</div>
		</div>
	)
}

function Loader(
	{ contents, state, doneContent }: { contents: Array<string>; state: 'pending' | 'done'; doneContent: string },
) {
	const [currentIndex, setCurrentIndex] = useState(0)
	const intervalRef = useRef<number | null>(null)

	// Reset to start when contents change
	useEffect(() => {
		setCurrentIndex(0)
	}, [contents])

	// Advance through contents based on state
	useEffect(() => {
		if (contents.length === 0) { return }

		if (intervalRef.current !== null) {
			window.clearInterval(intervalRef.current)
			intervalRef.current = null
		}

		intervalRef.current = window.setInterval(() => {
			setCurrentIndex((prev) => {
				if (contents.length === 0) { return 0 }

				const isLast = prev >= contents.length - 1

				if (state === 'pending') {
					return (prev + 1) % contents.length
				}

				// state === 'done' -> progress until last, then stop
				if (isLast) {
					if (intervalRef.current !== null) {
						window.clearInterval(intervalRef.current)
						intervalRef.current = null
					}
					return prev
				}
				return prev + 1
			})
		}, 1600)

		return () => {
			if (intervalRef.current !== null) {
				window.clearInterval(intervalRef.current)
				intervalRef.current = null
			}
		}
	}, [contents, state])

	const visibleContent = contents[currentIndex]

	return (
		<div className="flex flex-row items-center gap-2">
			<LoadIndicator state={state} />
			<AnimateChangeInHeight>
				<AnimatePresence mode="wait">
					{visibleContent && (
						<motion.div
							key={state === 'done' ? 'done' : visibleContent}
							initial={{ opacity: 0, y: 4 }}
							animate={{ opacity: 1, y: 0 }}
							exit={{ opacity: 0, y: -4 }}
							transition={{ duration: 0.2 }}
						>
							{state === 'done' ? doneContent : visibleContent}
						</motion.div>
					)}
				</AnimatePresence>
			</AnimateChangeInHeight>
		</div>
	)
}

function MultiLoader(
	{ loaders }: { loaders: MultiLoaderPayload },
) {
	return (
		<div className="flex flex-col items-star gap-2">
			{loaders.map((loader) => (
				<Loader
					key={loader.content}
					contents={[loader.content]}
					state={loader.state}
					doneContent={loader.doneContent}
				/>
			))}
		</div>
	)
}

function LoadIndicator({ state }: { state: 'pending' | 'done' }) {
	return (
		<div>
			<div
				className="size-2.5 bg-purple-600 to-transparent rounded-full flex items-center justify-center transition-all duration-200"
				style={{
					width: state === 'pending' ? '10px' : '8px',
					height: state === 'pending' ? '10px' : '8px',
					animation: state === 'pending' ? 'spin 1s linear infinite' : 'none',
					clipPath: state === 'pending'
						? 'polygon(0 0, 50% 50%, 100% 0, 100% 100%, 0 100%)'
						: 'polygon(0 0, 100% 0, 100% 100%, 0 100%)',
				}}
			>
				<div
					className="size-2 bg-grey-50 rounded-full transition-all duration-200"
					style={{
						width: state === 'pending' ? '8px' : '0',
						height: state === 'pending' ? '8px' : '0',
					}}
				/>
			</div>
		</div>
	)
}
